<template>
    <div class="create-key-form p-fluid pt-2">
        <div class="p-field">
            <label for="modal-key-name" class="form-label">API Key Name</label>
            <InputText
                id="modal-key-name"
                v-model="keyName"
                placeholder="Enter a name for this key"
                class="w-full"
                aria-required="true"
                autofocus
            />
            <small class="form-help-text"
                >Give your key a descriptive name to identify its
                purpose.</small
            >
        </div>
        <div class="p-field">
            <label for="modal-key-expiration" class="form-label"
                >Expiration (Optional)</label
            >
            <Calendar
                id="modal-key-expiration"
                v-model="keyExpiration"
                dateFormat="dd/mm/yy"
                placeholder="No expiration"
                :minDate="tomorrow"
                showIcon
                class="w-full"
            />
            <small class="form-help-text"
                >If not set, the key will never expire.</small
            >
        </div>

        <div v-if="isMcp" class="p-field">
            <label for="organization-select" class="form-label"
                >Organization</label
            >
            <Select
                id="organization-select"
                v-model="selectedOrganization"
                :options="organizations"
                optionLabel="name"
                placeholder="Select an organization"
                class="w-full"
                @change="onOrganizationChange"
            />
            <small class="form-help-text"
                >Select the organization you want to access with this API
                key.</small
            >
        </div>

        <div v-if="isMcp" class="p-field">
            <label for="stek-select" class="form-label">Stek</label>
            <Select
                id="stek-select"
                v-model="selectedStek"
                :options="steks"
                optionLabel="name"
                placeholder="Select a stek"
                class="w-full"
                :disabled="!selectedOrganization || loadingSteks"
            />
            <small v-if="loadingSteks" class="form-help-text">
                <i class="pi pi-spin pi-spinner mr-2"></i>Loading steks...
            </small>
            <small v-else class="form-help-text"
                >Select the stek you want to access with this API key.</small
            >
        </div>
    </div>
    <div class="buttons">
        <Button
            label="Cancel"
            icon="pi pi-times"
            class="p-button-text"
            @click="dialogRef?.close"
        />
        <Button
            label="Create API Key"
            icon="pi pi-check"
            @click="createApiKey"
            :disabled="!isFormValid || isLoading"
            :loading="isLoading"
            autofocus
        />
    </div>
</template>

<script setup lang="ts">
import * as services from "@/lib/openapi/gen/sdk.gen";
import { ref, computed, inject, Ref } from "vue";
import { useApiKeyStore, useApiKeyManager } from "@/stores/api-key-store";
import {
    Organization,
    useOrganizationsManager,
} from "@/stores/organizations-store";
import { useAuthenticationStore } from "@/stores/authentication-store";
import { createClient } from "@/lib/openapi/client";
import { type CreateApiKeyResult } from "@/stores/api-key-store";
import Calendar from "primevue/calendar";
import type { DynamicDialogInstance } from "primevue/dynamicdialogoptions";
import { Stek } from "@/stores/steks-store";

interface DialogData {
    isMcp: boolean;
}

const dialogRef = inject<Ref<DynamicDialogInstance> | undefined>("dialogRef");
const apiKeyStore = useApiKeyStore();
const { isLoading } = useApiKeyManager();
const { organizations } = useOrganizationsManager();

// Get dialog data
const dialogData = dialogRef!.value.data as DialogData;
const isMcp = ref(dialogData.isMcp || false);

// Form fields
const keyName = ref("");
const keyExpiration = ref<Date>();

// MCP specific fields
const selectedOrganization = ref<Organization>();
const selectedStek = ref<Stek>();
const steks = ref<Stek[]>([]);
const loadingSteks = ref(false);

// Get tomorrow's date for the minimum date in the calendar
const tomorrow = new Date();
tomorrow.setDate(tomorrow.getDate() + 1);

// Form validation
const isFormValid = computed(() => {
    if (!keyName.value) return false;

    if (isMcp.value) {
        return Boolean(selectedOrganization.value && selectedStek.value);
    }

    return true;
});

/**
 * Fetch steks for a specific organization
 *
 * Unlike the regular steks store, this allows fetching steks for any organization,
 * not just the currently selected one.
 *
 * @param organizationId - The ID of the organization to fetch steks for
 * @returns An array of Stek objects
 */
const fetchSteksByOrganization = async (
    organizationId: string
): Promise<Stek[]> => {
    if (!organizationId) {
        console.warn("Cannot fetch steks: No organization ID provided");
        return [];
    }

    isLoading.value = true;

    try {
        const directClient = createClient();

        // Add a request interceptor to manually add auth token
        directClient.interceptors.request.use(async (request) => {
            const { getToken } = useAuthenticationStore();
            const token = await getToken();

            if (token) {
                request.headers.set("Authorization", `Bearer ${token}`);
            }

            return request;
        });

        const controller = new AbortController();

        const { response, data } =
            await services.startStekStekMgtGetSteksByOrganizationStartPost({
                headers: {
                    "Organization": organizationId,
                },
                body: {},
                signal: controller.signal,
                client: directClient,
            });

        if (response.ok) {
            return data || [];
        } else {
            if (response.status === 0) {
                // Request was aborted
                return [];
            }

            throw new Error(`Request failed with status ${response.status}`);
        }
    } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") {
            // Request was aborted, ignore error
            return [];
        }

        console.error("Error fetching steks for organization:", err);
        return [];
    } finally {
        isLoading.value = false;
    }
};

// Load steks when organization changes
const onOrganizationChange = async () => {
    if (!selectedOrganization.value) {
        steks.value = [];
        selectedStek.value = undefined;
        return;
    }

    loadingSteks.value = true;
    try {
        steks.value = await fetchSteksByOrganization(
            selectedOrganization.value.identity
        );
    } catch (error) {
        console.error("Error loading steks:", error);
        steks.value = [];
    } finally {
        loadingSteks.value = false;
    }
};

const createApiKey = async () => {
    if (!isFormValid.value) return;

    try {
        const result = await apiKeyStore.createApiKey(
            keyName.value,
            keyExpiration.value
        );

        if (result) {
            dialogRef?.value.close({
                success: true,
                apiKey: result,
                mcpData: isMcp.value
                    ? {
                          organization: selectedOrganization.value,
                          stek: selectedStek.value,
                      }
                    : undefined,
            } as CreateApiKeyResult);
        }
    } catch (error) {
        console.error("Error creating API key:", error);
        dialogRef?.value.close({ success: false, error });
    }
};
</script>

<style scoped lang="scss">
.create-key-form {
    margin: 0;
    padding: 0.5rem 0 1rem;
}

.p-field {
    display: flex;
    flex-direction: column;
    margin-bottom: 1.5rem;
}

.form-label {
    margin-bottom: 0.75rem;
    font-weight: 600;
    display: block;
}

.form-help-text {
    display: block;
    color: #6c757d;
    margin-top: 0.75rem;
    font-size: 0.875rem;
}

.buttons {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
}

.w-full {
    width: 100%;
}

.mr-2 {
    margin-right: 0.5rem;
}
</style>
